import { deviceApi as api } from '@/redux/api/endpoints/device';
import { selectUnitById } from '@/redux/api/seletors/select-unit-by-id';
import { AppGetState } from '@/redux/store';
import { QueryHandler } from '@/redux/types/query-handler';
import { QuerySuccessHandler } from '@/redux/types/query-success-handler';
import { addItemRecipe } from '@/redux/utils/add-item-recipe';
import { removeItemRecipe } from '@/redux/utils/remove-item-recipe';
import { updateItemRecipe } from '@/redux/utils/update-item-recipe';
import { DeviceAssociatedWithUnit, DeviceUnassociated } from '@/types/device';
import { DeviceAssignedToOrganisation } from '@/types/device';
import { Unit } from '@/types/unit';
import { updateUnitWithOrganisation } from '@/utils/update-unit-with-organisation';
import { RemoveDeviceFromUnitQueryBody } from './types/query-body';
import { getUnassociatedDevicesQueryParams } from '../get-unassociated-devices';

export type RemoveDeviceFromUnitQueryArg = {
  device: DeviceAssociatedWithUnit;
};

export const removeDeviceFromUnitQueryBody = ({
  device: { id },
}: RemoveDeviceFromUnitQueryArg): RemoveDeviceFromUnitQueryBody => ({
  actionType: 'RemoveFromUnit',
  id,
});

export const strings = {
  loading: 'Removing device from unit...',
  success: 'Removed device from unit',
  error: 'Failed to remove device from unit',
};

export const handleResultRemoveDeviceFromUnit: QueryHandler<
  RemoveDeviceFromUnitQueryArg,
  void | string
> = async (queryArg, { queryFulfilled, ...api }) => {
  await queryFulfilled;
  dispatchSuccessPatchRemoveDeviceFromUnit(queryArg, api);
};

const dispatchSuccessPatchRemoveDeviceFromUnit: QuerySuccessHandler<
  RemoveDeviceFromUnitQueryArg
> = ({ device }, { dispatch, getState }) => {
  const { organisation } = device;
  const organisationId = organisation.id;

  const unit = selectUnitById(organisation, device.unit.id)(getState());

  if (!unit) {
    throw new Error(`unit ${device.unit.unitName} not found`);
  }

  const getUnassociatedDevicesQueryArg = getUnassociatedDevicesQueryParams(
    { organisationId },
    getState as AppGetState,
  );

  const assignedDevice: DeviceAssignedToOrganisation = {
    ...device,
    unit: undefined,
  };

  /**
   * Remove from unit --> remove from org devices list
   */
  dispatch(
    api.util.updateQueryData(
      'getDevicesInOrganisation',
      { organisation },
      updateItemRecipe<DeviceAssignedToOrganisation, 'id'>(
        'id',
        assignedDevice.id,
        {
          unit: undefined,
        },
      ),
    ),
  );

  /**
   * Remove from unit --> appear in unassociated devices list
   * TODO: Does this need both the org key and also a blank key if they are platform admin?
   */
  dispatch(
    api.util.updateQueryData(
      'getUnassociatedDevices',
      getUnassociatedDevicesQueryArg,
      addItemRecipe<DeviceUnassociated>(assignedDevice),
    ),
  );

  /**
   * Remove from unit --> remove from org associated devices list
   */
  dispatch(
    api.util.updateQueryData(
      'getAssociatedDevicesInOrganisation',
      { organisation },
      removeItemRecipe('id', device.id),
    ),
  );

  /**
   * Remove from unit --> remove from unit associated devices list
   */
  dispatch(
    api.util.updateQueryData(
      'getAssociatedDevicesInUnit',
      { unit: updateUnitWithOrganisation(unit, organisation) },
      removeItemRecipe('id', device.id),
    ),
  );

  const updatedDevicesList = [...unit.devices];
  const unitIndexToRemove = updatedDevicesList.findIndex(
    ({ id }) => id === device.id,
  );
  if (unitIndexToRemove > -1) {
    updatedDevicesList.splice(unitIndexToRemove, 1);
  }

  // Remove from unit.devices in unit
  dispatch(
    api.util.updateQueryData(
      'getUnits',
      { organisation },
      updateItemRecipe<Unit, 'id'>('id', unit.id, {
        devices: updatedDevicesList,
      }),
    ),
  );
};
